import { useState, useMemo } from 'react';
import { NavIcon } from '../../../shell/shared';
import { EncounterChartingTab } from './EncounterChartingTab';
import { FilesTab } from '../consultation/FilesTab';
import { BeforeAfterCompare } from './BeforeAfterCompare';
import { SavedBeforeAfterPreview } from './SavedBeforeAfterPreview';
import { SavedDrawPreview } from './SavedDrawPreview';
import { SavedToothChartPreview } from './SavedToothChartPreview';
import { DrawCharting } from './DrawCharting';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qPatientFilesByEncounter, type PatientFileRow } from '@helios/data-access';

// ─── Internal tabs per PRD.161 Section 7.2 ───

interface InternalTab {
  key: string;
  label: string;
  icon: string;
  enabled: boolean;
}

const CHARTING_TABS: InternalTab[] = [
  { key: 'all', label: 'All', icon: 'layout-grid', enabled: true },
  { key: 'tooth-chart', label: 'Tooth Chart', icon: 'edit', enabled: true },
  { key: 'images', label: 'Images', icon: 'camera', enabled: true },
  { key: 'before-after', label: 'Before / After', icon: 'camera', enabled: true },
  { key: 'draw', label: 'Draw', icon: 'edit', enabled: true },
];

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string;
  primary: string;
  /** Initial tab to open. Defaults to 'all'. */
  initialTab?: string;
  /** Pre-select a Before/After chart when opening with `initialTab='before-after'`. */
  initialBeforeAfterChartId?: string | null;
  /** Pre-select a Drawing chart when opening with `initialTab='draw'`. */
  initialDrawChartId?: string | null;
}

// ─── Component ───

export function DentalChartingHero({
  patientId,
  encounterId,
  visitId,
  primary,
  initialTab,
  initialBeforeAfterChartId,
  initialDrawChartId,
}: Props) {
  const [activeKey, setActiveKey] = useState(initialTab ?? 'all');
  const [activeChartId, setActiveChartId] = useState<string | null>(
    initialBeforeAfterChartId ?? null,
  );
  const [activeDrawChartId, setActiveDrawChartId] = useState<string | null>(
    initialDrawChartId ?? null,
  );

  return (
    <div>
      {/* ── Internal tab strip (PRD 7.2) ── */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '10px 16px 6px',
          overflowX: 'auto',
        }}
      >
        {CHARTING_TABS.map((tab) => {
          const isActive = activeKey === tab.key && tab.enabled;
          const isDisabled = !tab.enabled;

          return (
            <button
              key={tab.key}
              onClick={() => {
                if (tab.enabled) setActiveKey(tab.key);
              }}
              disabled={isDisabled}
              title={isDisabled ? 'Coming soon' : tab.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 12px',
                borderRadius: 'var(--radius-full)',
                border: isActive ? `1.5px solid ${primary}` : '1px solid var(--border)',
                background: isActive ? `${primary}0a` : 'transparent',
                color: isDisabled
                  ? 'var(--text-placeholder)'
                  : isActive
                    ? primary
                    : 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: isActive ? 600 : 500,
                cursor: isDisabled ? 'default' : 'pointer',
                opacity: isDisabled ? 0.45 : 1,
                transition: 'all 150ms',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <NavIcon name={tab.icon} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Active sub-view ── */}
      <div style={{ minHeight: 200 }}>
        {activeKey === 'all' && (
          <DentalAllView
            patientId={patientId}
            encounterId={encounterId}
            onOpenBeforeAfter={(chartId) => {
              setActiveChartId(chartId);
              setActiveKey('before-after');
            }}
            onOpenDraw={(chartId) => {
              setActiveDrawChartId(chartId);
              setActiveKey('draw');
            }}
            onOpenToothChart={() => setActiveKey('tooth-chart')}
          />
        )}
        {activeKey === 'tooth-chart' && (
          <EncounterChartingTab patientId={patientId} encounterId={encounterId} visitId={visitId} />
        )}
        {activeKey === 'images' && (
          <div style={{ padding: 'var(--sp-5)' }}>
            <FilesTab
              patientId={patientId}
              encounterId={encounterId}
              visitId={visitId || undefined}
              hideFilters
              compact
            />
          </div>
        )}
        {activeKey === 'before-after' && (
          <BeforeAfterCompare
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            initialChartId={activeChartId}
          />
        )}
        {activeKey === 'draw' && (
          <DrawCharting
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            initialChartId={activeDrawChartId}
          />
        )}
      </div>
    </div>
  );
}

// ─── Dental All: mixed visual overview (PRD.166) ───

function DentalAllView({
  patientId,
  encounterId,
  onOpenBeforeAfter,
  onOpenDraw,
  onOpenToothChart,
}: {
  patientId: string;
  encounterId: string;
  onOpenBeforeAfter: (chartId: string) => void;
  onOpenDraw: (chartId: string) => void;
  onOpenToothChart: (chartId: string) => void;
}) {
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );
  const images = useMemo(() => fileRows.filter((f) => f.kind === 'image' && f.url), [fileRows]);

  return (
    <div>
      {/* Saved Chartings section */}
      <DentalSectionLabel>Saved Chartings</DentalSectionLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 8,
          padding: '0 16px 8px',
        }}
      >
        <SavedToothChartPreview
          patientId={patientId}
          encounterId={encounterId}
          onOpen={onOpenToothChart}
        />
        <SavedBeforeAfterPreview
          patientId={patientId}
          encounterId={encounterId}
          onOpen={onOpenBeforeAfter}
        />
        <SavedDrawPreview patientId={patientId} encounterId={encounterId} onOpen={onOpenDraw} />
      </div>

      {/* Images section */}
      <DentalSectionLabel>Images</DentalSectionLabel>
      {images.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No images yet for this visit
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: 8,
            padding: '0 16px 16px',
          }}
        >
          {images.map((file) => (
            <div
              key={file.id}
              style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border)',
              }}
            >
              <img
                src={file.url!}
                alt={file.filename ?? ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {file.tag && file.tag !== 'other' && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 4,
                    left: 4,
                    padding: '1px 6px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                >
                  {file.tag}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DentalSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '12px 16px 8px',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </div>
  );
}
